import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { postLogDetails } from "../../../Services/APIs/appDetails";
import { checkClientByName, validateEmployeeEmail } from "../../../Services/APIs/patient";
import { generateRandomAlphaNumeric } from "../../../Utils/alphaNumeric";
import welcomeTeeth from "../../../assets/illustrations/welcomeTeeth.png";
import toothlensLogo from "../../../assets/illustrations/Toothlens_logo.png";

const COMPANY_BUCKET_MAP = {
  "ab": "ab", 
  "tl-star": "starhealth",
  "care": "care",
  "plum": "toothlens",
  "loop": "toothlens",
  "coversure": "toothlens"
};

const VALID_CAMERA_SIDES = ["front", "back"];

const PRESERVED_KEYS = [
  "usertpi",
  "cameraSide", 
  "company", 
  "companyBucket", 
  "scanType", 
  "isAutomated",
  "reloaded-star",
  "insuranceLoginEmail",
  "insuranceGroupCompany"
];

const LandingPage = () => {
  const { company = "tl-star", cameraside = "front" } = useParams();
  const navigate = useNavigate();
  const { images } = useSelector((state) => state.fullScan);
  const storedUid = JSON.parse(localStorage.getItem("usertpi") || '{"usertpi": ""}');
  const [uid, setUid] = useState(storedUid.usertpi);
  const [showError, setShowError] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidCompany, setIsValidCompany] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [membersList, setMembersList] = useState([]);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);
  const [memberFetchError, setMemberFetchError] = useState('');
  const [coveredUids, setCoveredUids] = useState([]);
  
  const resolvedCompany = COMPANY_BUCKET_MAP.hasOwnProperty(company) ? company : "tl-star";
  const resolvedCameraSide = VALID_CAMERA_SIDES.includes(cameraside) ? cameraside : "front";
  const bucket = "toothlens";

  // Validate company via API
  useEffect(() => {
    const validateCompany = async () => {
      setIsValidating(true);
      setShowError(false);
      
      try {
        const response = await checkClientByName(company);
        
        if (response.status === 200 && response.data?.data && typeof response.data.data === 'object' && response.data.data.client) {
          setIsValidCompany(true);
          setShowError(false);
        } else {
          setIsValidCompany(false);
          setShowError(true);
        }
      } catch (error) {
        console.error("Error validating company:", error);
        setIsValidCompany(false);
        setShowError(true);
      } finally {
        setIsValidating(false);
      }
    };

    validateCompany();
  }, [company]);

  // Decode auth parameter to extract email
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authData = urlParams.get('auth');
    
    if (authData) {
      try {
        const decoded = JSON.parse(atob(authData));
        const { email, company: decodedCompany } = decoded;
        
        if (email) {
          localStorage.setItem("insuranceLoginEmail", email);
        }
        if (decodedCompany) {
          localStorage.setItem("insuranceGroupCompany", decodedCompany);
        }
      } catch (error) {
      }
    }else{
        window.location.href = 'https://portal.toothlens.com/insurance/login';

    }
  }, []);

  useEffect(() => {
    cleanLocalStorageWithPreservation();
    localStorage.setItem("isAutomated", "true");
    localStorage.setItem("scanType", "tl-star");
    const isReloaded = JSON.parse(localStorage.getItem("reloaded-star") || "false");
    if (!isReloaded) {
      localStorage.setItem("reloaded-star", "true");
      window.location.reload();
      return;
    }
    if (!uid || images.length === 0) {
      generateUserInfo();
    }
  }, []);

  const cleanLocalStorageWithPreservation = () => {
    const backup = {};
    PRESERVED_KEYS.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        backup[key] = value;
      }
    });
    localStorage.clear();
    Object.entries(backup).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  };

  const generateUserInfo = useCallback(() => {
    // Generate 4 random alphabetic letters
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const alphaPrefix = Array.from({ length: 4 }, () => 
      letters.charAt(Math.floor(Math.random() * letters.length))
    ).join('');
    
    // Generate 8 digit number using timestamp + random for uniqueness
    const timestamp = Date.now().toString();
    const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const numericPart = (timestamp.slice(-5) + randomSuffix).slice(0, 8).padStart(8, '0');
    
    const uniqueId = alphaPrefix + numericPart;
    localStorage.setItem("usertpi", JSON.stringify({ usertpi: uniqueId }));
    localStorage.setItem("companyBucket", JSON.stringify({ companyBucket: bucket }));
    localStorage.setItem("cameraSide", resolvedCameraSide);
    localStorage.setItem("company", resolvedCompany);
    setUid(uniqueId);
    postLogDetails({ 
      uid: uniqueId, 
      app: `${resolvedCompany} of side ${resolvedCameraSide}`, 
      payLoad: `visited landing page` 
    });
  }, [resolvedCompany, resolvedCameraSide, bucket]);

  const proceedWithMember = (uid, name) => {
    if (!uid || uid.trim() === '') {
      setMemberFetchError('Invalid member ID. Please contact support.');
      return;
    }
    cleanLocalStorageWithPreservation();
    localStorage.setItem("isAutomated", "true");
    localStorage.setItem("reloaded-star", "true");
    localStorage.setItem("scanType", "tl-star");
    localStorage.setItem("usertpi", JSON.stringify({ usertpi: uid }));
    localStorage.setItem("companyBucket", JSON.stringify({ companyBucket: bucket }));
    localStorage.setItem("cameraSide", resolvedCameraSide);
    localStorage.setItem("company", resolvedCompany);
    setUid(uid);
    postLogDetails({
      uid,
      app: `${resolvedCompany} of side ${resolvedCameraSide}`,
      payLoad: `visited landing page - member: ${name}`
    });
    navigate('/group/dental-form');
  };

  const handleNewScan = async () => {
    const storedEmail = localStorage.getItem('insuranceLoginEmail');

    if (!storedEmail) {
      // No email from auth param — fall back to auto-generate
      cleanLocalStorageWithPreservation();
      localStorage.setItem("isAutomated", "true");
      localStorage.setItem("reloaded-star", "true");
      localStorage.setItem("scanType", "tl-star");
      generateUserInfo();
      navigate('/group/dental-form');
      return;
    }

    setIsFetchingMembers(true);
    setMemberFetchError('');

    try {
      const veFormData = new FormData();
      veFormData.append('email', storedEmail);
      veFormData.append('company', company);
      const veResponse = await validateEmployeeEmail(veFormData);
      const beneficiaries = veResponse.data?.data?.beneficiaries;
      const coveredUidsList = veResponse.data?.data?.beneficiaries_covered?.uids || [];
      setCoveredUids(coveredUidsList);

      if (!beneficiaries || !beneficiaries.uids || beneficiaries.uids.length === 0) {
        setMemberFetchError('No members found for this account. Please contact support.');
        setIsFetchingMembers(false);
        return;
      }

      const members = beneficiaries.uids
        .map((uid, idx) => ({
          uid: uid || '',
          name: beneficiaries.names?.[idx] || '',
          relationship: beneficiaries.relationships?.[idx] || '',
          gender: beneficiaries.genders?.[idx] || '',
          isCovered: uid ? coveredUidsList.includes(uid) : false,
          isInvalidUid: !uid || uid.trim() === ''
        }));

      setIsFetchingMembers(false);

      if (members.length === 0) {
        setMemberFetchError('Member data is incomplete. Please contact support.');
        return;
      }

      if (members.length === 1) {
        if (members[0].isCovered || members[0].isInvalidUid) {
          setMembersList(members);
          setShowMemberModal(true);
        } else {
          proceedWithMember(members[0].uid, members[0].name);
        }
      } else {
        setMembersList(members);
        setShowMemberModal(true);
      }
    } catch (error) {
      setMemberFetchError('Failed to fetch member details. Please try again.');
      setIsFetchingMembers(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-white px-6 py-8 flex flex-col text-gray-800 font-sans">
      {/* Loading State */}
      {isValidating && (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 bg-indigo-600 rounded-full opacity-20 animate-pulse"></div>
            </div>
          </div>
          <p className="text-gray-700 font-semibold text-xl mt-6 tracking-wide">Validating...</p>
        </div>
      )}

      {/* Error Message */}
      {!isValidating && showError && (
        <div className="flex flex-col items-center justify-center min-h-screen px-4">
          <div className="bg-red-50 border-2 border-red-300 text-red-800 px-8 py-6 rounded-2xl max-w-md shadow-lg">
            <div className="flex items-start gap-4">
              <div className="pt-1 flex-shrink-0">
                <svg className="fill-current h-8 w-8 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"/>
                </svg>
              </div>
              <div>
                <p className="font-bold text-xl mb-2">Invalid URL</p>
                <p className="text-base mt-2 leading-relaxed">
                  The company route you're trying to access is not valid. Please check the URL and try again.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Only show if validation passed */}
      {!isValidating && isValidCompany && (
        <>
          {/* Logo */}
          <div className="flex justify-center mb-10">
            <img
              src={toothlensLogo}
              alt="ToothLens Logo"
              className="w-40 h-auto drop-shadow-md"
            />
          </div>

          {/* Headings */}
          <div className="flex flex-col items-center text-center mb-12">
            <h1 className="text-4xl font-extrabold text-gray-900 mb-3 tracking-tight leading-tight">Welcome to Toothlens</h1>
            <h2 className="text-2xl font-semibold text-gray-700 tracking-wide">Let's Get you Started!</h2>
          </div>

          {/* Main Content */}
          <div className="flex flex-col items-center text-center flex-grow justify-center px-4">
            <div className="max-w-md">
              <p className="text-lg text-gray-700 leading-relaxed font-medium">
                 Verify your details and complete your AI Dental Scan to generate your UID and activate your plan. 
              </p>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="w-full flex flex-col justify-center py-6">
            <p className="text-base text-gray-600 text-center mb-8 font-medium">
              It only takes minutes with your phone—simple as that.
            </p>
            
            {memberFetchError && (
              <p className="text-red-600 text-sm text-center mb-4 font-medium">{memberFetchError}</p>
            )}

            {localStorage.getItem("isScanCompleted") ? (
              <div className="space-y-4">
                <p className="text-lg text-green-600 font-bold text-center">
                  You have successfully completed your scan 
                </p>
                <button
                  onClick={handleNewScan}
                  disabled={isFetchingMembers}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-xl text-white w-full h-14 font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isFetchingMembers ? 'Loading...' : 'Take New Scan'}
                </button>
              </div>
            ) : (
              <button
                onClick={handleNewScan}
                disabled={isFetchingMembers}
                className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-xl text-white w-full h-14 font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isFetchingMembers ? 'Loading...' : 'START'}
              </button>
            )}
          </div>
        </>
      )}

      {/* Member Selection Modal */}
      {showMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-4">
              <h2 className="text-white font-bold text-lg">Select Member</h2>
              <p className="text-indigo-200 text-sm mt-1">Choose a family member to proceed with the scan</p>
            </div>
            <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
              {[...membersList].sort((a, b) => (a.relationship?.toLowerCase() === 'self' ? -1 : b.relationship?.toLowerCase() === 'self' ? 1 : 0)).map((member, idx) => {
                const isCovered = member.isCovered;
                const isInvalid = member.isInvalidUid;
                const isDisabled = isCovered || isInvalid;
                return (
                  <button
                    key={member.uid || idx}
                    disabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) {
                        setShowMemberModal(false);
                        proceedWithMember(member.uid, member.name);
                      }
                    }}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-150 text-left
                      ${isInvalid
                        ? 'border-red-200 bg-red-50 opacity-80 cursor-not-allowed'
                        : isCovered
                          ? 'border-green-200 bg-green-50 opacity-70 cursor-not-allowed'
                          : 'border-gray-100 hover:border-indigo-400 hover:bg-indigo-50'
                      }`}
                  >
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${isInvalid ? 'bg-red-100' : isCovered ? 'bg-green-100' : 'bg-indigo-100'}`}>
                      <span className={`font-bold text-lg uppercase ${isInvalid ? 'text-red-500' : isCovered ? 'text-green-600' : 'text-indigo-700'}`}>
                        {member.name ? member.name.charAt(0) : '?'}
                      </span>
                    </div>
                    <div className="flex-grow min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`font-semibold capitalize truncate ${isInvalid ? 'text-red-700' : isCovered ? 'text-gray-500' : 'text-gray-900'}`}>
                          {member.name || 'Unknown Member'}
                        </p>
                        {isInvalid && (
                          <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full whitespace-nowrap">UID Invalid</span>
                        )}
                        {!isInvalid && isCovered && (
                          <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full whitespace-nowrap">✓ Scan Completed</span>
                        )}
                      </div>
                      {member.relationship && (
                        <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full capitalize ${isInvalid ? 'bg-red-100 text-red-600' : isCovered ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {member.relationship}
                        </span>
                      )}
                      {isInvalid && (
                        <p className="text-xs text-red-500 mt-1 font-medium">UID is not valid — kindly contact support</p>
                      )}
                      {!isInvalid && isCovered && (
                        <p className="text-xs text-gray-400 mt-1">Scan already submitted — no further action needed</p>
                      )}
                    </div>
                    {!isDisabled && (
                      <svg className="w-5 h-5 flex-shrink-0 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;